import { useCallback, useMemo } from "react";
import {
    PresenceChannelParams,
    PresenceKind,
    PresenceScope,
    getPresenceChannelSubject,
} from "@common/types/presence-nats";
import { useBusyClosesDemoViewerIds } from "@components/demo/busyClosesDemo/useBusyClosesDemoLive";
import { NO_USER_IDS, getPresenceTargetKey } from "./presence.lib";
import { getPresentUserIdsOfTarget } from "./presenceStore";
import { usePresenceChannelSnapshot } from "./usePresenceChannelSnapshot";

export interface PresenceViewersTarget {
    scope: PresenceScope;
    kind: PresenceKind;
    workspaceId: string;
    targetId: string;
}

/**
 * The other users currently present on a target. Every caller observing the
 * same scope and kind shares one subscription, so rows of a list can each call
 * this for their own target.
 */
export const usePresenceViewers = (
    target: PresenceViewersTarget | null,
): number[] => {
    const organizationId = target?.scope.organizationId;
    const scopeWorkspaceId = target?.scope.workspaceId;
    const kind = target?.kind;
    const workspaceId = target?.workspaceId;
    const targetId = target?.targetId;

    const channel = useMemo((): PresenceChannelParams | null => {
        if (!kind || !targetId) {
            return null;
        }

        const narrowedTargetId =
            kind === PresenceKind.CHAT ? targetId : undefined;
        if (organizationId) {
            return {
                scope: { organizationId },
                kind,
                targetId: narrowedTargetId,
            };
        }
        return scopeWorkspaceId
            ? {
                  scope: { workspaceId: scopeWorkspaceId },
                  kind,
                  targetId: narrowedTargetId,
              }
            : null;
    }, [organizationId, scopeWorkspaceId, kind, targetId]);

    const channelSubject = useMemo(
        () => (channel ? getPresenceChannelSubject(channel) : null),
        [channel],
    );
    const targetKey =
        workspaceId && targetId
            ? getPresenceTargetKey({ workspaceId, targetId })
            : null;

    const getSnapshot = useCallback(
        () =>
            channelSubject && targetKey
                ? getPresentUserIdsOfTarget({ channelSubject, targetKey })
                : NO_USER_IDS,
        [channelSubject, targetKey],
    );

    const presentUserIds = usePresenceChannelSnapshot(channel, getSnapshot);
    const demoViewerIds = useBusyClosesDemoViewerIds(
        kind === PresenceKind.CHAT ? null : (targetId ?? null),
    );
    return demoViewerIds ?? presentUserIds;
};
